// src/components/admin/ImageManager.jsx
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { storage } from '../../utils/firebase';

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';import '../../styles/images.css';

function ImageManager({ images = {}, onUpdate, salonId, onSave, saving, savedMessage, onPreview }) {
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('hero');
  const [uploadError, setUploadError] = useState(null);
  const [errors, setErrors] = useState({
    hero: '',
    gallery: ''
  });

  // Default image structure if not provided
  const imageData = {
    hero: images.hero || '',
    gallery: images.gallery || []
  };

  const validateHeroImage = (heroUrl) => {
    if (!heroUrl || heroUrl.trim() === '') {
      setErrors(prev => ({ ...prev, hero: 'Hero Image is mandatory. Please upload an image.' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, hero: '' }));
      return true;
    }
  };

  const validateGalleryImages = (gallery) => {
    if (!gallery || gallery.length === 0) {
      setErrors(prev => ({ ...prev, gallery: 'At least one Gallery Image is mandatory. Please upload images.' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, gallery: '' }));
      return true;
    }
  };

  const handleHeroImageDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    setUploadType('hero');
    setUploadError(null);
    
    try {
      const file = acceptedFiles[0];
      
      // Create a safe filename using timestamp
      const timestamp = new Date().getTime();
      const safeFileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, `salons/${salonId || 'default'}/hero/${safeFileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get permanent download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Hero image uploaded successfully. Download URL:', downloadURL);
      
      const updatedImages = {
        ...imageData,
        hero: downloadURL
      };

      console.log('=== HERO IMAGE DEBUG ===');
console.log('New hero URL:', downloadURL);
console.log('Updated images object:', updatedImages);
console.log('Calling onUpdate...');

      onUpdate(updatedImages);
      validateHeroImage(downloadURL);
      setUploading(false);
    } catch (error) {
      console.error('Error uploading hero image:', error);
      setUploadError('Failed to upload hero image: ' + error.message);
      setUploading(false);
    }
  };


  
  const handleGalleryImageDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    setUploadType('gallery');
    setUploadError(null);
    
    try {
      // Upload all images to Firebase Storage
      const uploadPromises = acceptedFiles.map(async (file) => {
        // Create a safe filename using timestamp
        const timestamp = new Date().getTime();
        const safeFileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
        
        // Upload to Firebase Storage
        const storageRef = ref(storage, `salons/${salonId || 'default'}/gallery/${safeFileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        
        // Get permanent download URL
        return getDownloadURL(snapshot.ref);
      });
      
      // Wait for all uploads to complete
      const downloadURLs = await Promise.all(uploadPromises);
      
      console.log('Gallery images uploaded successfully:', downloadURLs);
      
      const updatedImages = {
        ...imageData,
        gallery: [...imageData.gallery, ...downloadURLs]
      };

      onUpdate(updatedImages);
      validateGalleryImages(updatedImages.gallery);
      setUploading(false);
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      setUploadError('Failed to upload gallery images: ' + error.message);
      setUploading(false);
    }
  };
  
  const removeHeroImage = () => {
    const updatedImages = {
      ...imageData,
      hero: ''
    };

    onUpdate(updatedImages);
    validateHeroImage('');
  };

  const removeGalleryImage = (index) => {
    const updatedGallery = [...imageData.gallery];
    updatedGallery.splice(index, 1);

    const updatedImages = {
      ...imageData,
      gallery: updatedGallery
    };

    onUpdate(updatedImages);
    validateGalleryImages(updatedGallery);
  };
  
  const { getRootProps: getHeroRootProps, getInputProps: getHeroInputProps } = useDropzone({
    onDrop: handleHeroImageDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1
  });
  
  const { getRootProps: getGalleryRootProps, getInputProps: getGalleryInputProps } = useDropzone({
    onDrop: handleGalleryImageDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 5
  });

  return (
    <div className="image-manager">
      <div className="editor-page-header">
        <h2>Images</h2>
        <div className="editor-page-actions">
          {savedMessage && <span className={`editor-save-msg${savedMessage.toLowerCase().includes('error') ? ' error' : ''}`}>{savedMessage}</span>}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
          <button className="page-action-btn page-action-save" type="button" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
      
      <div className="form-group">
        <label>
          Hero Image (Banner) <span className="required-asterisk">*</span>
        </label>
        <p className="form-description">This image will appear as a banner at the top of your salon page.</p>
        {errors.hero && <span className="error-message">{errors.hero}</span>}
        <div {...getHeroRootProps()} className={`dropzone hero-dropzone ${errors.hero ? 'dropzone-error' : ''}`}>
          <input {...getHeroInputProps()} />
          {uploading && uploadType === 'hero' ? (
            <p>Uploading hero image to storage...</p>
          ) : uploadError && uploadType === 'hero' ? (
            <div className="upload-error">
              <p className="error-message">{uploadError}</p>
              <p>Try again or select a different file</p>
            </div>
          ) : imageData.hero ? (
            <div className="image-preview">
              <img 
                src={imageData.hero} 
                alt="Hero" 
                className="hero-preview-img" 
                onError={(e) => console.error("Failed to load hero image in editor:", e.target.src)}
              />
              <button 
                type="button" 
                className="remove-button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeHeroImage();
                }}
              >
                Remove Image
              </button>
              <p>Drag & drop a new image or click to replace</p>
            </div>
          ) : (
            <p>Drag & drop a hero image here, or click to select a file</p>
          )}
        </div>
      </div>
      
      <div className="form-group">
        <label>
          Gallery Images (Services/Staff) <span className="required-asterisk">*</span>
        </label>
        <p className="form-description">Add photos of your services, staff, or salon interior. Up to 5 images.</p>
        {errors.gallery && <span className="error-message">{errors.gallery}</span>}
        <div {...getGalleryRootProps()} className={`dropzone gallery-dropzone ${errors.gallery ? 'dropzone-error' : ''}`}>
          <input {...getGalleryInputProps()} />
          {uploading && uploadType === 'gallery' ? (
            <p>Uploading gallery images to storage...</p>
          ) : uploadError && uploadType === 'gallery' ? (
            <div className="upload-error">
              <p className="error-message">{uploadError}</p>
              <p>Try again or select different files</p>
            </div>
          ) : (
            <p>Drag & drop up to 5 gallery images here, or click to select files</p>
          )}
        </div>
        
        {imageData.gallery.length > 0 && (
          <div className="gallery-preview">
            <h4>Current Gallery Images</h4>
            <div className="gallery-grid">
              {imageData.gallery.map((image, index) => (
                <div key={index} className="gallery-item">
                  <img 
                    src={image} 
                    alt={`Gallery ${index + 1}`} 
                    onError={(e) => console.error(`Failed to load gallery image ${index} in editor:`, e.target.src)}
                  />
                  <button 
                    className="remove-button" 
                    onClick={() => removeGalleryImage(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageManager;